/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useContext, useEffect, useState } from 'react'
import {
    Button,
    Checkbox,
    Dropdown,
    Form,
    FormField,
    FormGroup,
    Header,
    Icon,
    Input,
    Loader,
    Segment,
    Tab,
} from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import {
    getCategorySet,
    getCategorySlot,
    getCategorySlots,
    setSlotOverride,
    updateCategorySet,
} from '../../services/Category/Category.service'
import CategoryList from './CategoryList'
import CategorySlot from './CategorySlot'
import CategoryAddSlot from './CategoryAddSlot'
import { UltyModalWrapperContext } from '../Shared/UltyModalWrapper/UltyModalWrapperContext'
import useUser from '../Shared/UserProvider/useUser'
import PropTypes from 'prop-types'
import useModified from './useModified'
import CategoryModifiedModal from './CategoryModifiedModal'
import { Prompt } from 'react-router-dom'
import useBreadcrumb from '../NavBar/useBreadcrumb'
import { isProviderAPos } from '../../services/Provider/Provider.service'
import { CategoryMergeFile } from './CategoryMergeFile'
import { useNavigate, useParams } from 'react-router-dom-v5-compat'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const HeaderSetEdit = ({set}) => {
    const [isEdit, setIsEdit] = useState(false)
    const [loading, setLoading] = useState(false)
    const [setName, setSetName] = useState(set?.name)

    useEffect(() => {
        setSetName(set?.name)
    }, [set])

    const handleSave = async () => {
        setLoading(true)
        await updateCategorySet(set.id, {name: setName})
        setIsEdit(false)
        setLoading(false)
    }

    if (!isEdit) {
        return (
          <div css={css`
              display: flex;
          `}>
              <Header>{setName}</Header>
              <Icon name="pencil" onClick={() => setIsEdit(true)} css={css` cursor: pointer;
                  margin-left: 10px !important; `}/>
          </div>
        )
    }

    return (
      <Form.Field>
          <div css={css`
              display: flex;
          `}>
              <Input
                loading={loading}
                name="setName"
                onChange={(evt, {value}) => setSetName(value)}
                value={setName}
              />
              <div css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  margin-left: 10px;
              `}>
                  <Button icon="check" circular size="mini" onClick={handleSave} loading={loading} disabled={loading}/>
              </div>
              <div css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
              `}>
                  <Button icon="times" circular size="mini" onClick={() => setIsEdit(false)} loading={loading}
                          disabled={loading}/>
              </div>
          </div>
      </Form.Field>
    )
}

HeaderSetEdit.propTypes = {
    set: PropTypes.object.isRequired,
}

const SetHeader = ({set}) => {
    const {user} = useUser()

    return (
      (user.provider.id === set.provider.id) ? (
        <HeaderSetEdit set={set}/>
      ) : (
        <Header>{set.name}</Header>
      )
    )
}

const CategorySetList = () => {
    const {user, canExtendCategories} = useUser()
    const {setPaths} = useBreadcrumb()
    const [t] = useTranslation()
    const [activeIndex, setActiveIndex] = useState(0)
    const {modified, setModified} = useModified()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const {id: setId, slotId} = useParams()

    const defineConfirmModal = (callback) => {
        const modalSettings = {
            title: t('categories.confirm_exit'),
            component: <CategoryModifiedModal
              afterSubmit={(result) => {
                  handleUltyModalWrapper(false, null)
                  if (result) {
                      setModified(false)
                      callback()
                  }
              }}
            />,
        }
        handleUltyModalWrapper(true, modalSettings)
    }

    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)
    const defineAddModal = () => {
        const modalSettings = {
            title: t('category_set.new_slot'),
            component: <CategoryAddSlot
              setId={setId}
              afterSubmit={async (slotId) => {
                  await queryClient.invalidateQueries({queryKey: ['set', setId, 'slots']})
                  navigate(`/categories/${setId}/slots/${slotId}`, {replace: true})
                  handleUltyModalWrapper(false, null)
              }}
            />,
        }

        handleUltyModalWrapper(true, modalSettings)
    }

    const defineMergeFileModal = () => {
        const modalSettings = {
            title: t('category_set.merge_file'),
            component: <CategoryMergeFile
              setId={setId}
              afterSubmit={async () => {
                  await queryClient.invalidateQueries({queryKey: ['set', setId]})
                  handleUltyModalWrapper(false, null)
              }}
            />,
        }

        handleUltyModalWrapper(true, modalSettings)
    }

    const invalidateSlot = async (slotId) => {
        await queryClient.invalidateQueries({queryKey: ['set', setId, 'slots', slotId]})
    }

    const setSlotOverrideMutation = useMutation({
        mutationFn: ({slotOverride}) => setSlotOverride(slotOverride),
        onMutate: async ({slotOverride}) => {
            await queryClient.cancelQueries({queryKey: ['set', setId, 'slots', slotOverride.slotId]})
            const previousSlot = queryClient.getQueryData(['set', setId, 'slots', slotOverride.slotId])

            queryClient.setQueryData(['set', setId, 'slots', slotOverride.slotId], prev => {
                return {
                    ...prev,
                    overrides: prev.overrides.map(override => {
                        if (override.providerId === user.provider.id) {
                            const isCurrentlyActive = override.activatedAt !== null && override.deactivatedAt === null

                            return {
                                ...override,
                                activatedAt: isCurrentlyActive ? slotOverride.activatedAt : new Date().toISOString(),
                                deactivatedAt: isCurrentlyActive ? new Date().toISOString() : null,
                            }
                        }
                        return override
                    }),
                }
            })

            return {previousSlot}
        },
        onError: (err, {slotOverride}, context) => {
            queryClient.setQueryData(['set', setId, 'slots', slotOverride.slotId], context.previousSlot)
        },
        onSettled: async () => {
            await invalidateSlot(slotOverride.slotId)
        },
    })

    const {data: set} = useQuery({
        queryKey: ['set', setId],
        queryFn: () => getCategorySet(setId),
    })

    const {data: slots, isSuccess: isGetCategorySlotsSuccess} = useQuery({
        queryKey: ['set', setId, 'slots'],
        queryFn: () => getCategorySlots(set.id),
        enabled: !!set,
    })

    const {data: slot} = useQuery({
        queryKey: ['set', setId, 'slots', slotId],
        queryFn: () => getCategorySlot(set.id, slotId),
        enabled: !!set && !!slotId,
        select: (data) => ({
            ...data,
            position: slots.find(s => s.id === data.id)?.position,
        })
    })

    useEffect(() => {
        if (isGetCategorySlotsSuccess && slots && slots.length > 0) {
            const slotExists = slots.some(s => s.id === slotId)
            const targetSlotId = slotExists ? slotId : slots[0].id
            setActiveIndex(0)
            navigate(`/categories/${setId}/slots/${targetSlotId}`, {replace: true})
        }
    }, [isGetCategorySlotsSuccess, navigate, setId, slotId, slots])

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('breadcrumb.categories'),
            link: true,
            path: '/categories',
        }, {
            text: set?.name || t('global.loading'),
            link: true,
            path: `/categories/${setId}`,
        }, {
            text: t('breadcrumb.edition'),
            link: false,
        }])
    }, [set?.name, setId, setPaths, t])

    if (!slot) {
        return <Loader active/>
    }

    const isSlotOwnedByCurrentProvider = slot.provider.id === user.provider.id

    const slotOverride = slot?.overrides?.find(override => override.providerId === user.provider.id)

    const isSlotCurrentlyActive = slotOverride ?
      (slotOverride.activatedAt !== null && slotOverride.deactivatedAt === null)
      : true

    return (
      <Form>
          <Prompt
            when={modified}
            message={t('categories.confirm_continue')}
          />

          {set ? <SetHeader set={set}/> : <Loader active/>}
          <Segment color={(isProviderAPos(user.provider) && isSlotOwnedByCurrentProvider) ? 'green' : 'blue'}>
              <FormGroup widths="equal">
                  {slots?.length > 1 && (
                    <FormField inline>
                        <label>{t('category_set.slot_list')}</label>
                        <Dropdown
                          selection
                          value={slotId}
                          name="slots"
                          onChange={(evt, {value}) => {
                              const callback = () => {
                                  setActiveIndex(0)
                                  navigate(`/categories/${setId}/slots/${value}`, {replace: true})
                              }
                              if (modified) {
                                  defineConfirmModal(callback)
                              } else {
                                  callback()
                              }
                          }}
                          options={slots.map(s => ({
                              text: s.name,
                              value: s.id,
                              key: s.id,
                          }))}
                          placeholder={t('categories.slot')}
                        />
                    </FormField>
                  )}
                  {canExtendCategories() && <Form.Field inline css={css`text-align: right;`}>
                      <Button.Group color="teal">
                          <Button icon onClick={() => defineAddModal()}>
                              <Icon name="copy"/>
                              {t('category_set.new_slot')}
                          </Button>
                            <Dropdown
                              className="button icon"
                              trigger={<></>}
                            >
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                      key="merge"
                                      value="merge"
                                      icon="file"
                                      text={t('category_set.merge_file')}
                                      onClick={defineMergeFileModal}
                                    />
                                </Dropdown.Menu>
                            </Dropdown>
                      </Button.Group>
                  </Form.Field>}
              </FormGroup>
              {(isProviderAPos(user.provider) && canExtendCategories() && !isSlotOwnedByCurrentProvider && slots?.length > 1) && (
                <FormGroup>
                    <FormField
                      inline
                      css={css`
                          display: flex;
                          align-items: center;
                      `}>
                        <label>Activer ce sous-menu de l'enseigne</label>
                        <Checkbox
                          toggle
                          checked={isSlotCurrentlyActive}
                          onChange={() => {
                              setSlotOverrideMutation.mutate({
                                  slotOverride: {
                                      categorySetId: set.id,
                                      slotId: slot.id,
                                      activatedAt: isSlotCurrentlyActive ? null : new Date().toISOString(),
                                      deactivatedAt: isSlotCurrentlyActive ? new Date().toISOString() : null,
                                  },
                              })
                          }}
                        />
                    </FormField>
                </FormGroup>
              )}
              <div css={css`
                  font-style: italic;
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  justify-content: flex-start;
                  padding: 10px 0;
                  padding-top: 0;
              `}>
                  {t('product_list.num_items', {n: `${slot.itemsCount || 0}`})}
              </div>
          </Segment>
          {slot && (
            <Tab
              menu={{secondary: true, pointing: true}}
              activeIndex={activeIndex}
              onTabChange={(evt, {activeIndex}) => {
                  setActiveIndex(activeIndex)
              }}
              panes={[{
                  menuItem: t('category_set.slot_categories'),
                  render: () => (
                    <Segment>
                        <div css={css`margin-top: 10px;`}>
                            <CategoryList slot={slot}/>
                        </div>
                    </Segment>
                  ),
              }, ...(slots?.length > 1 ? [{
                  menuItem: t('category_set.manage_slot'),
                  render: () => (
                    <Segment>
                        <div css={css`margin-top: 10px;`}>
                            <CategorySlot
                              setId={setId}
                              slot={slot}
                              nbSlots={slots.length}
                              reload={async () => invalidateSlot(slotId)}
                            />
                        </div>
                    </Segment>
                  ),
              }] : [])]}
            />
          )}
      </Form>
    )
}

export default CategorySetList
